"use client";

import { Suspense } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { Home, Calendar, Star, Trophy, Flag, Menu, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// Zwevende onderbalk als pill met oranje rand (prototype-stijl), iconen only.
// De livestream-tab is vervangen door Home: er is geen livestreampagina en een
// knop die niets doet roept vragen op tijdens een demo.
//
// Het klassement heeft sinds 11 oktober 2026 een eigen knop, als tweede (C6).
// Joost: de stand is waar iedereen - zeker elke deelnemer - als eerste kijkt,
// en hij zat achter een tabblad van Uitslagen. Die twee kregen daardoor elk
// een eigen icoon: de beker voor de stand, de finishvlag voor de uitslagen.
type Tab = { id: string; label: string; icon: LucideIcon; href: string };

const tabs: Tab[] = [
  { id: "home", label: "Home", icon: Home, href: "/" },
  { id: "klassement", label: "Klassement", icon: Trophy, href: "/uitslagen?tab=standen" },
  { id: "kalender", label: "Kalender", icon: Calendar, href: "/kalender" },
  // Favorieten bij het midden: dat is de plek waar Flashscore ze ook zet, en
  // het is de reden dat iemand de app buiten wedstrijddagen opent.
  { id: "favorieten", label: "Favorieten", icon: Star, href: "/favorieten" },
  { id: "uitslagen", label: "Uitslagen", icon: Flag, href: "/uitslagen" },
  { id: "meer", label: "Meer", icon: Menu, href: "/meer" },
];

function actieveTab(pad: string, tab: string | null): string | null {
  if (pad === "/") return "home";
  if (pad.startsWith("/uitslagen")) return tab === "standen" ? "klassement" : "uitslagen";
  if (pad.startsWith("/klassement")) return "klassement";
  if (pad.startsWith("/kalender") || pad.startsWith("/wedstrijd")) return "kalender";
  if (pad.startsWith("/favorieten")) return "favorieten";
  if (["/meer", "/nieuws", "/video", "/live", "/zoeken", "/over-ons", "/meldingen", "/reglementen"].some((p) => pad.startsWith(p)))
    return "meer";
  return null;
}

// De zoekparameters lezen kan pas in de browser; Next wil dat in een Suspense.
// Tot die er zijn, beslist het pad alleen.
function Balk({ tab }: { tab: string | null }) {
  const actief = actieveTab(usePathname(), tab);
  return (
    <div className="flex w-full items-center justify-around gap-1 rounded-full border-2 border-primary bg-card/70 px-3 py-3 backdrop-blur-md">
      {tabs.map(({ id, label, icon: Icon, href }) => {
        const aan = actief === id;
        return (
          <Link
            key={id}
            href={href}
            aria-label={label}
            aria-current={aan ? "page" : undefined}
            className="flex flex-1 items-center justify-center py-1"
          >
            <Icon
              className={cn(aan ? "size-7 text-primary" : "size-6 text-muted-foreground")}
              strokeWidth={aan ? 2.4 : 2}
            />
          </Link>
        );
      })}
    </div>
  );
}

function BalkMetZoek() {
  return <Balk tab={useSearchParams().get("tab")} />;
}

export function BottomNav() {
  return (
    // pb houdt rekening met de veilige zone: de app draait schermvullend op het
    // beginscherm (viewportFit cover), dus zonder dit valt de balk onder de
    // home-indicator van de iPhone.
    <nav className="fixed bottom-0 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 justify-center bg-gradient-to-t from-background via-background/95 to-transparent px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-8">
      <Suspense fallback={<Balk tab={null} />}>
        <BalkMetZoek />
      </Suspense>
    </nav>
  );
}
